import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import './Profile.css';

// helper: base64url -> Uint8Array
const base64urlToBuffer = (base64url) => {
  if (!base64url) return base64url;
  if (base64url instanceof ArrayBuffer) return base64url;
  if (ArrayBuffer.isView(base64url)) {
    return base64url.buffer.slice(base64url.byteOffset, base64url.byteOffset + base64url.byteLength);
  }
  if (typeof base64url === 'object') {
    if (Array.isArray(base64url)) return Uint8Array.from(base64url).buffer;
    if (base64url.data !== undefined) return base64urlToBuffer(base64url.data);
    return base64url;
  }
  if (typeof base64url !== 'string') return base64url;
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));
  const binary = atob(base64 + pad);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

function Profile() {
  const { token, getProfile, updateProfile, registerPasskeyRequest, registerPasskeyResponse } = useAuth();
  const { t } = useI18n();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaQr, setMfaQr] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaMessage, setMfaMessage] = useState('');
  const [passkeyMessage, setPasskeyMessage] = useState('');
  const [passkeyName, setPasskeyName] = useState('');
  const [showPkNamePrompt, setShowPkNamePrompt] = useState(false);

  useEffect(() => {
    const load = async () => {
      const p = await getProfile();
      setProfile(p);
      if (p) {
        setDisplayName(p.displayName || '');
        setEmail(p.email || '');
      }
    };
    load();
  }, [getProfile]);

  const save = async () => {
    setLoading(true);
    const res = await updateProfile({ displayName, email });
    if (res.success) {
      setProfile(res.user);
      setEditing(false);
    }
    setLoading(false);
  };

  // MFA setup flow
  const startMfaSetup = async () => {
    setMfaMessage('');
    try {
      const res = await fetch('/api/users/mfa/setup', { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start MFA setup');
      setMfaQr(data.qr || null);
    } catch (err) {
      setMfaMessage(err.message);
    }
  };

  const enableMfa = async () => {
    setMfaMessage('');
    try {
      const res = await fetch('/api/users/mfa/enable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code: mfaCode })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to enable MFA');
      setMfaMessage(t('app.accountSettings.mfa.enabled') || 'MFA enabled');
    } catch (err) {
      setMfaMessage(err.message);
    }
  };

  // Passkey registration
  const registerPasskey = async () => {
    if (!showPkNamePrompt) {
      setShowPkNamePrompt(true);
      return;
    }
    setPasskeyMessage('');
    try {
      if (!passkeyName.trim()) {
        setPasskeyMessage(t('app.accountSettings.passkeys.nameRequired') || 'Passkey name is required');
        return;
      }
      const start = await registerPasskeyRequest();
      if (!start || !start.success) throw new Error(start.error || 'Failed to get registration options');
      const options = start.options;
      // Convert challenge and user.id
      options.challenge = base64urlToBuffer(options.challenge);
      if (options.user && options.user.id) options.user.id = base64urlToBuffer(options.user.id);
      if (Array.isArray(options.excludeCredentials)) {
        options.excludeCredentials = options.excludeCredentials.map(c => ({ ...c, id: base64urlToBuffer(c.id) }));
      }
      const cred = await navigator.credentials.create({ publicKey: options });
      if (!cred) throw new Error('Credential creation failed');
      // Prepare attestation for server
      const attestation = {
        id: cred.id,
        rawId: btoa(String.fromCharCode(...new Uint8Array(cred.rawId))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''),
        response: {
          clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(cred.response.clientDataJSON))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''),
          attestationObject: btoa(String.fromCharCode(...new Uint8Array(cred.response.attestationObject))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
        },
        type: cred.type,
        name: passkeyName.trim()
      };
      const finish = await registerPasskeyResponse(attestation);
      if (!finish || !finish.success) throw new Error(finish.error || 'Failed to register passkey');
      setPasskeyMessage(t('app.accountSettings.passkeys.registered') || 'Passkey registered');
      setShowPkNamePrompt(false);
      setPasskeyName('');
    } catch (err) {
      setPasskeyMessage(err.message || String(err));
      setShowPkNamePrompt(false);
    }
  };

  if (!profile) return <div>{t('app.loading') || 'Loading...'}</div>;

  return (
    <div className="profile-page">
      <h2>My Profile</h2>
      <form className="cp-form" onSubmit={(e) => { e.preventDefault(); save(); }}>
        <div className="cp-field">
          <label>Username</label>
          <div>{profile.username}</div>
        </div>

        <div className="cp-field">
          <label>Display name</label>
          {editing ? (
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          ) : (
            <div>{profile.displayName || ''}</div>
          )}
        </div>

        <div className="cp-field">
          <label>Email</label>
          {editing ? (
            <input value={email} onChange={(e) => setEmail(e.target.value)} />
          ) : (
            <div>{profile.email || ''}</div>
          )}
        </div>

        <div className="cp-actions">
          {editing ? (
            <>
              <button className="cp-btn-primary" type="submit" disabled={loading}>Save</button>
              <button type="button" className="cp-btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
            </>
          ) : (
            <button type="button" className="cp-btn-primary" onClick={() => setEditing(true)}>Edit profile</button>
          )}
        </div>
      </form>

      <hr />

      <h3>{t('app.accountSettings.tabs.mfa') || 'MFA (TOTP)'}</h3>
      <div className="cp-form">
        {mfaQr ? (
          <div className="cp-field">
            <div dangerouslySetInnerHTML={{ __html: `<img src="${mfaQr}" alt="MFA QR" />` }} />
            <p>{t('app.accountSettings.mfa.setupHelp') || 'Scan the QR with your authenticator app and enter the code to enable.'}</p>
            <input className="" placeholder="123456" value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} />
            <div style={{ marginTop: 8 }}>
              <button className="cp-btn-primary" onClick={enableMfa}>{t('app.accountSettings.mfa.enable') || 'Enable MFA'}</button>
            </div>
            <div className="muted">{mfaMessage}</div>
          </div>
        ) : (
          <div className="cp-field">
            <button className="cp-btn-primary" onClick={startMfaSetup}>{t('app.accountSettings.mfa.setup') || 'Setup MFA (TOTP)'}</button>
            <div className="muted">{mfaMessage}</div>
          </div>
        )}
      </div>

      <hr />

      <h3>{t('app.accountSettings.tabs.passkeys') || 'Passkeys'}</h3>
      <div className="cp-form">
        {showPkNamePrompt ? (
          <div className="cp-field">
            <label>{t('app.accountSettings.passkeys.nameLabel') || 'Passkey name'}</label>
            <input
              placeholder={t('app.accountSettings.passkeys.namePlaceholder') || 'e.g., My MacBook Pro'}
              value={passkeyName}
              onChange={(e) => setPasskeyName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && registerPasskey()}
            />
            <div className="muted">{t('app.accountSettings.passkeys.nameHelp') || 'Give your passkey a descriptive name (e.g., device name)'}</div>
            <div className="cp-actions" style={{ marginTop: 12 }}>
              <button type="button" className="cp-btn-secondary" onClick={() => { setShowPkNamePrompt(false); setPasskeyName(''); }}>{t('app.accountSettings.passkeys.cancel') || 'Cancel'}</button>
              <button type="button" className="cp-btn-primary" onClick={registerPasskey}>{t('app.accountSettings.passkeys.continue') || 'Continue'}</button>
            </div>
          </div>
        ) : (
          <div className="cp-field">
            <button className="cp-btn-primary" onClick={registerPasskey}>{t('app.accountSettings.passkeys.registerNew') || 'Register new passkey'}</button>
            <div className="muted">{passkeyMessage}</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Profile;
